import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Phone, TrendingUp, AlertCircle, Activity, Users } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const API_BASE_URL = 'http://localhost:5000/api/dashboard';

const AppointmentsDashboard = () => {
  const [scheduleData, setScheduleData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [scheduleResponse, statsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/today-tomorrow-upcoming-10`),
        fetch(`${API_BASE_URL}/stats`)
      ]);
      
      if (!scheduleResponse.ok || !statsResponse.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const schedule = await scheduleResponse.json();
      const stats = await statsResponse.json();
      
      setScheduleData(schedule);
      setStatsData(stats);
    } catch (err) {
      setError(err.message);
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'Scheduled': 'bg-blue-50 text-blue-700 border-blue-200',
      'Completed': 'bg-green-50 text-green-700 border-green-200',
      'Cancelled': 'bg-red-50 text-red-700 border-red-200',
      'Pending': 'bg-yellow-50 text-yellow-700 border-yellow-200'
    };
    return statusStyles[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getTypeBadge = (type) => {
    return type === 'appointment' 
      ? 'bg-purple-50 text-purple-700 border-purple-200' 
      : 'bg-cyan-50 text-cyan-700 border-cyan-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl border border-red-100 p-8 max-w-md w-full">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Unable to Load Dashboard</h3>
          <p className="text-slate-600 text-center mb-6">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-medium shadow-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Chart Data Preparation
  const totalApptsVisits = [
    { name: 'Appointments', value: statsData?.totalAppointments || 0, color: '#8b5cf6' },
    { name: 'Visits', value: statsData?.totalVisits || 0, color: '#06b6d4' }
  ];

  const monthlyComparison = [
    { period: 'This Month', Appointments: statsData?.thisMonthAppointments || 0, Visits: statsData?.thisMonthVisits || 0 },
    { period: 'This Year', Appointments: statsData?.thisYearAppointments || 0, Visits: statsData?.thisYearVisits || 0 }
  ];

  const statusBreakdown = [
    { category: 'Today', Appointments: statsData?.todaysAppointments || 0, Visits: statsData?.todaysVisits || 0 },
    { category: 'Upcoming', Appointments: statsData?.upcomingAppointments || 0, Visits: statsData?.upcomingVisits || 0 },
    { category: 'Past Pending', Appointments: statsData?.pastPendingAppointments || 0, Visits: statsData?.pastPendingVisits || 0 }
  ];

  const CHART_COLORS = ['#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444'];

  const ScheduleCard = ({ item }) => (
    <div className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-xl p-4 hover:shadow-lg transition-all duration-200 hover:scale-[1.02]">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-slate-600" />
            <span className="font-semibold text-slate-900">{item.contact.name}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{item.time}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Phone className="w-3.5 h-3.5" />
            <span>{item.contact.mobile}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <span className={`text-xs px-2.5 py-1 rounded-lg border font-medium ${getTypeBadge(item.type)}`}>
            {item.type}
          </span>
          <span className={`text-xs px-2.5 py-1 rounded-lg border font-medium ${getStatusBadge(item.status)}`}>
            {item.status}
          </span>
        </div>
      </div>
      <div className="pt-3 border-t border-slate-200">
        <p className="text-sm text-slate-700">
          <span className="font-semibold">Reason:</span> {item.reason}
        </p>
        {item.notes && (
          <p className="text-xs text-slate-500 mt-1 italic">{item.notes}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-[1600px] mx-auto">
        
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Medical Dashboard</h1>
          <p className="text-slate-600 text-lg">Appointments & Visits Management System</p>
        </div>

        {/* Stats Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <Calendar className="w-6 h-6" />
              </div>
              <span className="text-3xl font-bold">{(statsData?.todaysAppointments || 0) + (statsData?.todaysVisits || 0)}</span>
            </div>
            <h3 className="text-blue-100 text-sm font-medium mb-1">Today's Schedule</h3>
            <p className="text-sm opacity-90">{statsData?.todaysAppointments || 0} Appointments • {statsData?.todaysVisits || 0} Visits</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <TrendingUp className="w-6 h-6" />
              </div>
              <span className="text-3xl font-bold">{(statsData?.upcomingAppointments || 0) + (statsData?.upcomingVisits || 0)}</span>
            </div>
            <h3 className="text-purple-100 text-sm font-medium mb-1">Upcoming</h3>
            <p className="text-sm opacity-90">{statsData?.upcomingAppointments || 0} Appointments • {statsData?.upcomingVisits || 0} Visits</p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 via-cyan-600 to-cyan-700 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <Activity className="w-6 h-6" />
              </div>
              <span className="text-3xl font-bold">{(statsData?.thisMonthAppointments || 0) + (statsData?.thisMonthVisits || 0)}</span>
            </div>
            <h3 className="text-cyan-100 text-sm font-medium mb-1">This Month</h3>
            <p className="text-sm opacity-90">{statsData?.thisMonthAppointments || 0} Appointments • {statsData?.thisMonthVisits || 0} Visits</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <AlertCircle className="w-6 h-6" />
              </div>
              <span className="text-3xl font-bold">{(statsData?.pastPendingAppointments || 0) + (statsData?.pastPendingVisits || 0)}</span>
            </div>
            <h3 className="text-orange-100 text-sm font-medium mb-1">Past Pending</h3>
            <p className="text-sm opacity-90">{statsData?.pastPendingAppointments || 0} Appointments • {statsData?.pastPendingVisits || 0} Visits</p>
          </div>
        </div>

        {/* Analytics Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          
          {/* Pie Chart */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Total Distribution
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={totalApptsVisits}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}\n${(percent * 100).toFixed(0)}%`}
                  outerRadius={90}
                  dataKey="value"
                >
                  {totalApptsVisits.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-sm text-slate-600">Appointments: {statsData?.totalAppointments || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                <span className="text-sm text-slate-600">Visits: {statsData?.totalVisits || 0}</span>
              </div>
            </div>
          </div>

          {/* Bar Chart - Monthly */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-cyan-600" />
              Monthly Overview
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Legend />
                <Bar dataKey="Appointments" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Visits" fill="#06b6d4" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart - Status */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Status Breakdown
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statusBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="category" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Legend />
                <Bar dataKey="Appointments" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Visits" fill="#06b6d4" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Schedule Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Today */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Today
              </h2>
              <span className="bg-blue-100 text-blue-700 text-sm font-bold px-3 py-1.5 rounded-lg">
                {scheduleData?.today?.count || 0}
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-4 font-medium">{scheduleData?.today?.date}</p>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {scheduleData?.today?.items?.length > 0 ? (
                scheduleData.today.items.map(item => <ScheduleCard key={item.appointmentId || item.visitId} item={item} />)
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 font-medium">No items scheduled</p>
                </div>
              )}
            </div>
          </div>

          {/* Tomorrow */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Tomorrow
              </h2>
              <span className="bg-purple-100 text-purple-700 text-sm font-bold px-3 py-1.5 rounded-lg">
                {scheduleData?.tomorrow?.count || 0}
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-4 font-medium">{scheduleData?.tomorrow?.date}</p>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {scheduleData?.tomorrow?.items?.length > 0 ? (
                scheduleData.tomorrow.items.map(item => <ScheduleCard key={item.appointmentId || item.visitId} item={item} />)
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 font-medium">No items scheduled</p>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
                Upcoming
              </h2>
              <span className="bg-cyan-100 text-cyan-700 text-sm font-bold px-3 py-1.5 rounded-lg">
                {scheduleData?.upcoming?.length || 0}
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-4 font-medium">Next 10 days</p>
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {scheduleData?.upcoming?.length > 0 ? (
                scheduleData.upcoming.map((item) => (
                  <div key={item.appointmentId || item.visitId} className="bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-xl p-4 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-slate-600" />
                          <span className="font-semibold text-slate-900">{item.contact.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 font-medium mb-2">{formatDate(item.date)}</div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{item.time}</span>
                        </div>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-lg border font-medium ${getTypeBadge(item.type)}`}>
                        {item.type}
                      </span>
                    </div>
                    <div className="pt-3 border-t border-slate-200">
                      <p className="text-sm text-slate-700">
                        <span className="font-semibold">Reason:</span> {item.reason}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-400 font-medium">No upcoming items</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsDashboard;